import React, {useCallback, useEffect, useState} from 'react';
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {Ionicons} from '@expo/vector-icons';
import {useTranslation} from 'react-i18next';
import PosBottomSheet from './PosBottomSheet';
import {NUMPAD_KEYS, applyNumpadKey, parseNumpadValue} from '../../helpers/posNumpad';
import {useTheme} from '../../helpers/ThemeContext';
import {haptics} from '../../helpers/haptics';

const ROWS = [['1', '2', '3'], ['4', '5', '6'], ['7', '8', '9']];

/**
 * Big on-screen numeric keypad in a bottom sheet — replaces the OS keyboard for
 * quantities and cash amounts (no keyboard jumping, 72dp keys, one-handed).
 *
 * @param {object} props
 * @param {boolean} props.visible Whether the sheet is shown.
 * @param {function(): void} props.onClose Close without saving.
 * @param {function(number): void} props.onConfirm Called with the parsed number.
 * @param {string} [props.title] Sheet title.
 * @param {string|number} [props.initialValue=''] Draft the keypad opens with.
 * @param {boolean} [props.allowDecimal=false] Show the decimal key.
 * @param {number} [props.maxLength=9] Max characters.
 * @param {string} [props.suffix] Unit shown after the value ("Ft", "pcs").
 * @param {number[]} [props.quickValues] Preset chips that replace the draft ("1000", "5000"…).
 * @param {function(number): boolean} [props.validate] Extra validation (e.g. `n >= 1`); defaults to "> 0".
 * @returns {JSX.Element}
 */
const PosNumpadSheet = ({visible, onClose, onConfirm, title, initialValue = '', allowDecimal = false, maxLength = 9, suffix, quickValues, validate}) => {
    const {t} = useTranslation();
    const {theme} = useTheme();
    const [draft, setDraft] = useState('');

    useEffect(() => {
        if (visible) {
            setDraft(initialValue === null || initialValue === undefined ? '' : String(initialValue));
        }
    }, [visible, initialValue]);

    const press = useCallback((key) => {
        haptics.select();
        setDraft((current) => applyNumpadKey(current, key, {maxLength, allowDecimal}));
    }, [allowDecimal, maxLength]);

    const parsed = parseNumpadValue(draft);
    const valid = Number.isFinite(parsed) && (validate ? validate(parsed) : parsed > 0);

    const confirm = () => {
        if (!valid) {
            haptics.error();

            return;
        }

        haptics.tap();
        onConfirm(parsed);
    };

    const Key = ({label, keyId, icon}) => (
        <TouchableOpacity
            activeOpacity={0.7}
            onPress={() => press(keyId)}
            onLongPress={keyId === NUMPAD_KEYS.BACK ? () => press(NUMPAD_KEYS.CLEAR) : undefined}
            accessibilityRole="button"
            accessibilityLabel={label || keyId}
            style={[styles.key, {backgroundColor: theme.colors.inputBackground || theme.colors.background, borderColor: theme.colors.border}]}
        >
            {icon ? <Ionicons name={icon} size={28} color={theme.colors.text} /> : <Text style={[styles.keyText, {color: theme.colors.text}]}>{label}</Text>}
        </TouchableOpacity>
    );

    return (
        <PosBottomSheet
            visible={visible}
            onClose={onClose}
            title={title}
            footer={(
                <TouchableOpacity
                    activeOpacity={0.85}
                    onPress={confirm}
                    accessibilityRole="button"
                    style={[styles.confirm, {backgroundColor: valid ? theme.colors.primary : theme.colors.border}]}
                >
                    <Text style={styles.confirmText}>{t('confirm')}</Text>
                </TouchableOpacity>
            )}
        >
            <View style={[styles.display, {borderColor: theme.colors.border}]}>
                <Text style={[styles.displayText, {color: theme.colors.text}]} numberOfLines={1}>{draft === '' ? '0' : draft}</Text>
                {!!suffix && <Text style={[styles.suffix, {color: theme.colors.subText}]}>{suffix}</Text>}
            </View>
            {!!quickValues?.length && (
                <View style={styles.chips}>
                    {quickValues.map((value) => (
                        <TouchableOpacity
                            key={value}
                            activeOpacity={0.8}
                            onPress={() => {
                                haptics.select();
                                setDraft(String(value));
                            }}
                            style={[styles.chip, {borderColor: theme.colors.primary}]}
                        >
                            <Text style={[styles.chipText, {color: theme.colors.primary}]}>{value}</Text>
                        </TouchableOpacity>
                    ))}
                </View>
            )}
            {ROWS.map((row) => (
                <View key={row.join('')} style={styles.row}>
                    {row.map((digit) => <Key key={digit} label={digit} keyId={digit} />)}
                </View>
            ))}
            <View style={styles.row}>
                {allowDecimal ? <Key label="." keyId={NUMPAD_KEYS.DOT} /> : <Key label="C" keyId={NUMPAD_KEYS.CLEAR} />}
                <Key label="0" keyId="0" />
                <Key icon="backspace-outline" keyId={NUMPAD_KEYS.BACK} label="backspace" />
            </View>
        </PosBottomSheet>
    );
};

const styles = StyleSheet.create({
    display: {
        flexDirection: 'row',
        alignItems: 'baseline',
        justifyContent: 'flex-end',
        borderWidth: StyleSheet.hairlineWidth,
        borderRadius: 16,
        paddingHorizontal: 16,
        paddingVertical: 10,
        marginBottom: 10,
    },
    displayText: {fontSize: 44, fontWeight: '900'},
    suffix: {fontSize: 20, fontWeight: '700', marginLeft: 8},
    chips: {flexDirection: 'row', flexWrap: 'wrap', marginBottom: 6},
    chip: {
        minHeight: 48,
        minWidth: 76,
        borderWidth: 1.5,
        borderRadius: 24,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 14,
        marginRight: 8,
        marginBottom: 8,
    },
    chipText: {fontSize: 18, fontWeight: '800'},
    row: {flexDirection: 'row', marginBottom: 8},
    key: {
        flex: 1,
        height: 66,
        borderRadius: 16,
        borderWidth: StyleSheet.hairlineWidth,
        alignItems: 'center',
        justifyContent: 'center',
        marginHorizontal: 4,
    },
    keyText: {fontSize: 30, fontWeight: '800'},
    confirm: {
        minHeight: 64,
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 6,
    },
    confirmText: {color: '#fff', fontSize: 21, fontWeight: '900'},
});

export default PosNumpadSheet;
